import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Battery, Gauge, AlertTriangle, Check, X } from 'lucide-react';
import './GuidePage.css';

export default function EBikeGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>⚡ E-Bike Basics: 25 km/h & 45 km/h</h1>
        
        <div className="info-box">
          <Zap size={24} />
          <div>
            <strong>E-Bikes machen Schottland für alle erreichbar!</strong> Auch die steilen Highlands 
            werden mit elektrischer Unterstützung zum Genuss statt zur Qual.
          </div>
        </div>

        <section>
          <h2>1. Die zwei E-Bike-Klassen</h2>
          
          <h3>Pedelec 25 km/h (EPACs - Electrically Power Assisted Cycles)</h3>
          <div className="comparison-box green">
            <h4>✓ Pedelec 25 (Standard E-Bike)</h4>
            <ul>
              <li><strong>Unterstützung bis:</strong> 25 km/h</li>
              <li><strong>Motorleistung:</strong> Max. 250 Watt (nominal)</li>
              <li><strong>UK-Klassifizierung:</strong> EAPC (kein Motorfahrzeug)</li>
              <li><strong>Mindestalter:</strong> 14 Jahre</li>
              <li><strong>Führerschein:</strong> Nicht erforderlich</li>
              <li><strong>Versicherung:</strong> Nicht erforderlich</li>
              <li><strong>Kennzeichen:</strong> Nicht erforderlich</li>
              <li><strong>Helm:</strong> Keine Pflicht (aber dringend empfohlen!)</li>
              <li><strong>Radwege:</strong> Erlaubt</li>
            </ul>
          </div>

          <h3>S-Pedelec 45 km/h (Speed Pedelecs)</h3>
          <div className="comparison-box orange">
            <h4>⚠️ S-Pedelec 45 (Schnelles E-Bike)</h4>
            <ul>
              <li><strong>Unterstützung bis:</strong> 45 km/h</li>
              <li><strong>Motorleistung:</strong> Bis 4000 Watt (faktisch)</li>
              <li><strong>UK-Klassifizierung:</strong> Moped / Motor Vehicle</li>
              <li><strong>Mindestalter:</strong> 16 Jahre (17 in Northern Ireland)</li>
              <li><strong>Führerschein:</strong> AM-Klasse oder höher ERFORDERLICH</li>
              <li><strong>Versicherung:</strong> PFLICHT (Third Party Insurance)</li>
              <li><strong>Kennzeichen:</strong> PFLICHT (Vehicle Registration)</li>
              <li><strong>Helm:</strong> PFLICHT (Motorrad-Helm nach ECE 22.05)</li>
              <li><strong>Radwege:</strong> VERBOTEN (nur Straße erlaubt)</li>
              <li><strong>MOT-Test:</strong> Nach 3 Jahren jährlich erforderlich</li>
            </ul>
          </div>

          <div className="warning-box">
            <AlertTriangle size={24} />
            <strong>Wichtig für Schottland-Touren:</strong><br />
            S-Pedelecs (45 km/h) sind in UK als Motorfahrzeuge klassifiziert und dürfen NICHT auf 
            Radwegen, Forestry Tracks oder vielen Tourenrouten gefahren werden. Für Radtouren in 
            Schottland sind <strong>25 km/h Pedelecs die richtige Wahl!</strong>
          </div>
        </section>

        <section>
          <h2>2. Rechtliche Situation UK/Schottland</h2>
          
          <h3>Pedelec 25 km/h in UK</h3>
          <p>
            In Großbritannien gelten Pedelecs bis 25 km/h und 250W als normale Fahrräder (EAPC). 
            Du benötigst:
          </p>
          <ul>
            <li><Check className="check-icon" /> Kein Führerschein</li>
            <li><Check className="check-icon" /> Keine Versicherung</li>
            <li><Check className="check-icon" /> Kein Kennzeichen</li>
            <li><Check className="check-icon" /> Nutzung aller Radwege erlaubt</li>
            <li><Check className="check-icon" /> Zugang zu Forestry Tracks & Off-Road Trails</li>
          </ul>

          <h3>Technische Anforderungen EAPC</h3>
          <ul>
            <li>Motor schaltet bei 25 km/h ab (max. 15.5 mph)</li>
            <li>Nur Tretunterstützung (Pedalling muss erfolgen)</li>
            <li>Anfahrhilfe bis 6 km/h erlaubt (ohne Treten)</li>
            <li>Motor max. 250W (kontinuierliche Nennleistung)</li>
            <li>Bike muss Hersteller-Label haben (Leistung, Typ)</li>
          </ul>

          <h3>S-Pedelec 45 km/h in UK</h3>
          <p><strong>Gilt als Motorfahrzeug!</strong> Du benötigst:</p>
          <ul>
            <li><X className="x-icon" /> Führerschein Klasse AM (Moped)</li>
            <li><X className="x-icon" /> Haftpflichtversicherung (Third Party)</li>
            <li><X className="x-icon" /> Kennzeichen-Registrierung (Number Plate)</li>
            <li><X className="x-icon" /> Motorrad-Helm (ECE 22.05)</li>
            <li><X className="x-icon" /> KEINE Radwege (nur Straße)</li>
            <li><X className="x-icon" /> MOT-Test nach 3 Jahren</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Tipp für Schweizer/EU-Bürger</h3>
            <p>
              Wenn du dein eigenes S-Pedelec mitbringen möchtest, brauchst du in UK:
            </p>
            <ul>
              <li>Internationalen Führerschein</li>
              <li>UK-Versicherung (temporäre Cover möglich)</li>
              <li>Registrierung als Imported Vehicle</li>
            </ul>
            <p><strong>Empfehlung:</strong> Miete lieber ein 25 km/h Pedelec vor Ort!</p>
          </div>
        </section>

        <section>
          <h2>3. Akku & Reichweite</h2>
          
          <h3>Akkukapazität verstehen</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>300-400 Wh:</strong> Kleinere Akkus (40-70 km Reichweite)
            </div>
            <div className="data-item">
              <strong>500-625 Wh:</strong> Standard (70-120 km Reichweite)
            </div>
            <div className="data-item">
              <strong>700-900 Wh:</strong> Große Akkus (100-150+ km Reichweite)
            </div>
          </div>

          <h3>Faktoren die Reichweite beeinflussen</h3>
          <div className="info-box">
            <h4><Battery size={20} /> Reichweiten-Killer in Schottland</h4>
            <ul>
              <li><strong>Höhenmeter:</strong> -50% Reichweite in Highlands (viele Steigungen!)</li>
              <li><strong>Wind:</strong> -30% bei starkem Gegenwind (häufig an der Küste)</li>
              <li><strong>Kälte:</strong> -20% bei unter 10°C (auch im Sommer möglich)</li>
              <li><strong>Gewicht:</strong> -15% mit schwerem Gepäck</li>
              <li><strong>Untergrund:</strong> -25% auf Schotter/Trails vs. Asphalt</li>
              <li><strong>Reifendruck:</strong> -10% bei zu niedrigem Druck</li>
            </ul>
          </div>

          <h3>Reichweiten-Planung für Schottland</h3>
          <p><strong>Realistische Werte für Highland-Touren:</strong></p>
          <ul>
            <li><strong>500 Wh Akku:</strong> Rechne mit 50-70 km (nicht 100 km!)</li>
            <li><strong>625 Wh Akku:</strong> Rechne mit 60-90 km</li>
            <li><strong>Zwei 500 Wh Akkus:</strong> Ideal für 100+ km Tagesetappen</li>
          </ul>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>NC500 & Highlands-Warnung:</strong><br />
            Die Höhenmeter reduzieren die Reichweite drastisch! Für eine 80 km Etappe mit 
            1200 Höhenmetern brauchst du mindestens 625 Wh, besser einen Ersatzakku!
          </div>
        </section>

        <section>
          <h2>4. Laden in Schottland</h2>
          
          <h3>Lademöglichkeiten</h3>
          <ul>
            <li><strong>Unterkünfte:</strong> Alle B&Bs, Hotels haben normale UK-Steckdosen (Typ G)</li>
            <li><strong>Pubs & Cafés:</strong> Meist bereit, dein E-Bike zu laden (freundlich fragen!)</li>
            <li><strong>Tourist Information:</strong> Oft kostenlose Lademöglichkeit</li>
            <li><strong>Fahrradgeschäfte:</strong> Laden lassen während du einkaufst</li>
            <li><strong>Öffentliche Ladestationen:</strong> Selten für E-Bikes, meist nur E-Autos</li>
          </ul>

          <h3>UK-Stecker (Typ G)</h3>
          <div className="info-box">
            <h4>🔌 Adapter erforderlich!</h4>
            <p>
              UK nutzt den Stecker-Typ G (3-polig). Du brauchst einen Adapter für dein 
              EU/CH-Ladegerät!
            </p>
            <ul>
              <li>Kaufe 2-3 Adapter (als Backup)</li>
              <li>Kosten: ca. £5-10 im Supermarkt/Flughafen</li>
              <li>Spannung: 230V (wie EU/CH) - kein Spannungswandler nötig</li>
            </ul>
          </div>

          <h3>Ladezeiten</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>2A Ladegerät:</strong> 500 Wh in 5-6 Stunden
            </div>
            <div className="data-item">
              <strong>4A Ladegerät:</strong> 500 Wh in 2.5-3 Stunden
            </div>
            <div className="data-item">
              <strong>6A Fast-Charger:</strong> 500 Wh in 1.5-2 Stunden
            </div>
          </div>

          <h3>Lade-Strategie für Mehrtagestouren</h3>
          <ul>
            <li><strong>Abends:</strong> Sofort nach Ankunft ans Ladegerät (über Nacht voll)</li>
            <li><strong>Mittagspause:</strong> 1-2h Laden im Pub kann 20-30% nachladen</li>
            <li><strong>Zwei Akkus:</strong> Einen laden, einen fahren (optimale Strategie)</li>
            <li><strong>Powerbank:</strong> Funktioniert NICHT für E-Bike Akkus (zu wenig Leistung)</li>
          </ul>
        </section>

        <section>
          <h2>5. Unterstützungsstufen richtig nutzen</h2>
          
          <h3>Typische Modi</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>ECO:</strong> 30-50% Unterstützung - Max. Reichweite
            </div>
            <div className="data-item">
              <strong>TOUR:</strong> 80-100% - Komfort & Effizienz
            </div>
            <div className="data-item">
              <strong>SPORT:</strong> 150-180% - Sportlich, höherer Verbrauch
            </div>
            <div className="data-item">
              <strong>TURBO:</strong> 250-340% - Max. Power, hoher Verbrauch
            </div>
          </div>

          <h3>Strategie für maximale Reichweite</h3>
          <ul>
            <li><strong>Flach:</strong> ECO-Modus oder ganz ohne Motor</li>
            <li><strong>Leichte Steigung:</strong> TOUR-Modus</li>
            <li><strong>Steile Steigung:</strong> SPORT/TURBO nur wenn nötig</li>
            <li><strong>Downhill:</strong> Motor aus, Akku schonen</li>
            <li><strong>Gegenwind:</strong> Einen Modus niedriger als sonst</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Pro-Tipp: Gänge nutzen!</h3>
            <p>
              Viele E-Bike-Einsteiger fahren nur im TURBO-Modus mit schweren Gängen. 
              <strong>Besser:</strong> Leichte Gänge + niedrigere Unterstützung = mehr Reichweite!
            </p>
            <p>
              Faustregel: <strong>70-90 Trittfrequenz</strong> (wie beim normalen Rad) ist optimal.
            </p>
          </div>
        </section>

        <section>
          <h2>6. Gewicht & Transport</h2>
          
          <h3>E-Bike Gewicht</h3>
          <ul>
            <li><strong>Trekking E-Bike:</strong> 22-26 kg</li>
            <li><strong>E-MTB:</strong> 23-28 kg</li>
            <li><strong>Leichte E-Bikes:</strong> 16-20 kg (teurer)</li>
            <li><strong>Mit Gepäck:</strong> +5-15 kg (Taschen, Ausrüstung)</li>
          </ul>

          <h3>Transport nach Schottland</h3>
          <div className="comparison-box">
            <h4>✈️ Flugzeug</h4>
            <ul>
              <li><strong>Akku:</strong> Meist NICHT im Frachtraum erlaubt (max. 160 Wh Handgepäck)</li>
              <li><strong>Lösung:</strong> E-Bike vor Ort mieten!</li>
              <li><strong>Alternative:</strong> Akku separat per Spedition (kompliziert & teuer)</li>
            </ul>

            <h4>🚗 Auto/Van</h4>
            <ul>
              <li><strong>Heckträger:</strong> Spezielle E-Bike Träger nötig (höhere Traglast)</li>
              <li><strong>Dachträger:</strong> Wegen Gewicht schwierig</li>
              <li><strong>Im Auto:</strong> Vordersitz umklappen, Akku separat (Sicherheit)</li>
            </ul>

            <h4>🚂 Bahn</h4>
            <ul>
              <li><strong>UK Trains:</strong> E-Bikes wie normale Räder behandelt</li>
              <li><strong>Reservation:</strong> Oft erforderlich (bes. ScotRail)</li>
              <li><strong>Akku:</strong> Am Bike lassen ist OK</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>7. E-Bike Miete in Schottland</h2>
          
          <h3>Miet-Anbieter</h3>
          <ul>
            <li><strong>Bike Shops:</strong> Fast jede größere Stadt hat E-Bike-Verleih</li>
            <li><strong>Tour-Veranstalter:</strong> Oft im Paket mit geführten Touren</li>
            <li><strong>Spezialisierte Vermieter:</strong> E-Bike Scotland, Highland E-Bikes</li>
          </ul>

          <h3>Mietkosten</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>1 Tag:</strong> £40-60
            </div>
            <div className="data-item">
              <strong>Woche:</strong> £200-300
            </div>
            <div className="data-item">
              <strong>2 Wochen:</strong> £350-500
            </div>
            <div className="data-item">
              <strong>Ersatzakku:</strong> +£10-15/Tag
            </div>
          </div>

          <h3>Was ist inkludiert?</h3>
          <ul>
            <li><Check className="check-icon" /> E-Bike mit Akku & Ladegerät</li>
            <li><Check className="check-icon" /> Helm (meist)</li>
            <li><Check className="check-icon" /> Basis-Werkzeugset</li>
            <li><Check className="check-icon" /> Pannenhilfe-Nummer</li>
            <li><X className="x-icon" /> Versicherung (optional zubuchbar)</li>
            <li><X className="x-icon" /> Gepäcktaschen (meist extra)</li>
          </ul>

          <h3>Vorteile Miete</h3>
          <ul>
            <li>Kein Transport-Problem (Akku im Flugzeug)</li>
            <li>Keine Sorge bei Defekt (Ersatzbike verfügbar)</li>
            <li>Lokales Service-Netzwerk</li>
            <li>Aktuelles Equipment (neueste Modelle)</li>
          </ul>
        </section>

        <section>
          <h2>8. Wartung & Pannen</h2>
          
          <h3>Vor der Tour checken</h3>
          <div className="checklist">
            <div className="check-item">✓ Akku voll geladen</div>
            <div className="check-item">✓ Alle Steckverbindungen fest (Display, Akku, Motor)</div>
            <div className="check-item">✓ Software up-to-date (bei App-gesteuerten Bikes)</div>
            <div className="check-item">✓ Kette geschmiert (E-Bikes haben höheren Verschleiß)</div>
            <div className="check-item">✓ Bremsen prüfen (wichtig bei höherem Gewicht!)</div>
            <div className="check-item">✓ Reifendruck korrekt (bar-Angabe beachten)</div>
          </div>

          <h3>Häufige E-Bike Probleme</h3>
          <ul>
            <li><strong>Motor gibt keinen Support:</strong> Akku-Kontakte reinigen, Steckverbindung prüfen</li>
            <li><strong>Display zeigt Fehlercode:</strong> Bike aus/an, wenn bleibt: Service kontaktieren</li>
            <li><strong>Akku lädt nicht:</strong> Adapter prüfen, Akku-Kontakte reinigen</li>
            <li><strong>Reichweite plötzlich niedrig:</strong> Reifendruck, kaltes Wetter, Modus prüfen</li>
          </ul>

          <h3>Notfall-Strategie bei Akku leer</h3>
          <ul>
            <li><strong>Ohne Motor fahren:</strong> Geht (aber schwerer wegen Gewicht)</li>
            <li><strong>Schieben:</strong> Bei Steigungen manchmal einzige Option</li>
            <li><strong>Taxi/Van:</strong> In Schottland meist hilfsbereit (Trinkgeld geben)</li>
            <li><strong>Notruf bei Tour-Operator:</strong> Begleitfahrzeug holt dich ab</li>
          </ul>
        </section>

        <section>
          <h2>9. E-Bike & Umwelt</h2>
          
          <h3>Nachhaltigkeit</h3>
          <p><strong>E-Bikes sind deutlich umweltfreundlicher als Autos:</strong></p>
          <ul>
            <li>1 kWh Strom = 100-150 km E-Bike Reichweite</li>
            <li>CO2-Ausstoß: ca. 5g/km (vs. Auto 150g/km)</li>
            <li>Geringerer Flächenverbrauch</li>
            <li>Kein Lärm</li>
          </ul>

          <h3>Akku-Entsorgung</h3>
          <p>
            Lithium-Akkus müssen fachgerecht entsorgt werden. In UK gibt es Recycling-Programme:
          </p>
          <ul>
            <li><strong>Bike Shops:</strong> Nehmen alte Akkus zurück</li>
            <li><strong>Recycling Centres:</strong> Kommunale Sammelstellen</li>
            <li><strong>Hersteller:</strong> Rücknahme-Programme (Bosch, Shimano, etc.)</li>
          </ul>
        </section>

        <section>
          <h2>10. E-Bike vs. Bio-Bike</h2>
          
          <h3>Wann macht ein E-Bike Sinn?</h3>
          <ul>
            <li><strong>Steigungen:</strong> Highlands mit 10%+ Steigungen werden angenehm</li>
            <li><strong>Längere Strecken:</strong> 80-100 km/Tag ohne Überanstrengung</li>
            <li><strong>Gegenwind:</strong> Häufig in Schottland - E-Bike gleicht aus</li>
            <li><strong>Gepäck:</strong> Schweres Bikepacking-Gepäck wird leichter</li>
            <li><strong>Kondition:</strong> Ermöglicht Touren auch mit unterschiedlicher Fitness</li>
            <li><strong>Zeit:</strong> Schneller ans Ziel (wichtig bei limitiertem Urlaub)</li>
          </ul>

          <h3>Wann ist Bio-Bike besser?</h3>
          <ul>
            <li><strong>Gewicht:</strong> Leichter zu transportieren</li>
            <li><strong>Unabhängigkeit:</strong> Keine Ladestationen nötig</li>
            <li><strong>Einfachheit:</strong> Weniger Technik, weniger Fehler</li>
            <li><strong>Kosten:</strong> Günstiger in Anschaffung & Miete</li>
            <li><strong>Sportlichkeit:</strong> Mehr Training, intensiveres Erlebnis</li>
          </ul>
        </section>

        <div className="pro-tip">
          <h3>🎯 Die 10 E-Bike Goldenen Regeln für Schottland</h3>
          <ol>
            <li>Wähle 25 km/h Pedelec (nicht 45 km/h)</li>
            <li>Mind. 500 Wh Akku, besser 625 Wh</li>
            <li>UK-Adapter für Ladegerät besorgen</li>
            <li>Reichweite: Rechne mit -50% in Highlands</li>
            <li>Abends sofort laden</li>
            <li>Eco-Modus + niedrige Gänge = mehr Reichweite</li>
            <li>Ersatzakku bei langen Etappen (100+ km)</li>
            <li>E-Bike vor Ort mieten (kein Flug-Stress)</li>
            <li>Kette öfter schmieren (höherer Verschleiß)</li>
            <li>Bremsen prüfen (mehr Gewicht!)</li>
          </ol>
        </div>

        <div className="download-section">
          <h3>📥 E-Bike Guide herunterladen</h3>
          <button className="download-btn" onClick={() => window.print()}>
            E-Bike Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
